import { Flex, Link, StackProps, Text, Tooltip } from "@chakra-ui/react";
import { useMemo } from "react";

import { highlightStringMatch } from "@/common/components/Autocomplete/AutocompleteSelect";
import { CustomIconEmojiDisplay } from "@/common/components/Icons";
import { LinearConnectionIcon } from "@/integration/components/linear/LinearConnectionIcon";
import { resolveLinearIconAndColor } from "@reflag/shared/icons";
import { LinearTrackingEntity } from "@reflag/shared/linearConnectionAPI";

export type LinearTrackingEntityDisplayProps = StackProps & {
  entity?: LinearTrackingEntity;
  showIcon?: boolean;
  showTypeIcon?: boolean;
  search?: string;
  link?: boolean;
  showTooltip?: boolean;
};

export function LinearTrackingEntityDisplay({
  entity,
  search,
  showIcon = true,
  showTypeIcon = true,
  link = false,
  showTooltip = false,
  ...rest
}: LinearTrackingEntityDisplayProps) {
  const identifier = entity?.identifier ?? "Project";
  const { icon, iconColor } = useMemo(
    () => resolveLinearIconAndColor(entity),
    [entity],
  );

  const content = entity ? (
    <>
      {showIcon && <LinearConnectionIcon />}
      {showTypeIcon &&
        (entity.type === "issue" ? (
          <CustomIconEmojiDisplay iconKey="Issue" />
        ) : (
          <CustomIconEmojiDisplay
            color={iconColor}
            iconKey={icon ?? "Project"}
          />
        ))}

      <Tooltip
        isDisabled={!showTooltip}
        label={`${identifier} ${entity.title}`}
        openDelay={300}
        hasArrow
      >
        <Flex gap={1} isTruncated>
          <Text color="dimmed" whiteSpace="nowrap">
            {search ? highlightStringMatch(identifier, search) : identifier}
          </Text>
          <Text isTruncated>
            {search ? highlightStringMatch(entity.title, search) : entity.title}
          </Text>
        </Flex>
      </Tooltip>
    </>
  ) : (
    <>
      {showIcon && <LinearConnectionIcon isDisabled />}
      <Text color="dimmed" whiteSpace="nowrap">
        No issue/project
      </Text>
    </>
  );

  return (
    <Flex alignItems={"center"} gap={2} width="full" isTruncated {...rest}>
      {link && entity?.url ? (
        <Link
          _hover={{ textDecoration: "underline" }}
          alignItems={"center"}
          display="inline-flex"
          gap={2}
          href={entity.url}
          target="_blank"
          textDecoration="none"
          isTruncated
        >
          {content}
        </Link>
      ) : (
        content
      )}
    </Flex>
  );
}
